<script lang="ts">
  import { onDestroy } from 'svelte'
  import Search from '@lucide/svelte/icons/search'
  import X from '@lucide/svelte/icons/x'
  import Loader2 from '@lucide/svelte/icons/loader-2'
  import { clickOutside } from '../utils/actions/clickOutside'
  import { escapeKey } from '../utils/actions/escapeKey'
  import { createGlobalSearch } from '@/utils/globalSearch.svelte'
  import GlobalSearchResults from './GlobalSearchResults.svelte'

  let {
    applicationId,
    indexName,
  }: {
    applicationId: string
    indexName: string
  } = $props()

  const search = createGlobalSearch({
    applicationId: () => applicationId,
    indexName: () => indexName,
  })
  let searchInputRef = $state<HTMLInputElement | null>(null)

  onDestroy(() => search.destroy())

  function openSearch() {
    search.open()
    setTimeout(() => searchInputRef?.focus(), 0)
  }

  function closeSearch() {
    search.close()
  }
</script>

{#if search.showSearch}
  <div
    class="flex items-center gap-2 relative"
    use:clickOutside={closeSearch}
    use:escapeKey={closeSearch}
  >
    <div class="relative">
      <Search
        size={16}
        class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
      />
      <input
        bind:this={searchInputRef}
        type="text"
        bind:value={search.query}
        oninput={() => search.handleInput()}
        placeholder="Search..."
        class="w-64 sm:w-80 pl-9 pr-10 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
      {#if search.isSearching}
        <Loader2
          size={16}
          class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"
        />
      {:else if search.query}
        <button
          type="button"
          onclick={() => {
            search.query = ''
            search.resetResults()
          }}
          class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          aria-label="Clear search"
        >
          <X size={16} />
        </button>
      {/if}
    </div>
    <button
      type="button"
      onclick={closeSearch}
      class="p-1.5 text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-gray-700 rounded-md transition"
      aria-label="Close search"
    >
      <X size={18} />
    </button>

    {#if search.mergedResults.length > 0 || search.isSearchingBooks || search.bookSearchError}
      <div
        class="absolute left-0 top-full mt-1 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto z-50"
      >
        <GlobalSearchResults
          items={search.mergedResults}
          layout="desktop"
          isSearchingBooks={search.isSearchingBooks}
          error={search.bookSearchError}
          onSelectHit={(hit) => search.selectResult(hit)}
          onSelectExternal={(book) => search.selectExternal(book)}
        />
      </div>
    {:else if search.hasNoResults}
      <div
        class="absolute left-0 top-full mt-1 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 z-50"
      >
        <p class="text-gray-500 dark:text-gray-400 text-sm text-center">
          No catalog or Google Books results for "{search.query}". Try a title,
          author, series, or genre.
        </p>
      </div>
    {/if}
  </div>
{:else}
  <button
    type="button"
    onclick={openSearch}
    class="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm font-medium text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-gray-700 rounded-md transition"
    aria-label="Search"
  >
    <Search size={18} />
  </button>
{/if}
